import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FamilyLinkType, Sex } from '@bciti/types';
import { UserService } from '../../service/user.abstract.service';
import { PostUserFamilyLinkPayload, UserRecord } from '../../user.model';
import { InformationalDialogComponent, ErrorSeverity } from 'src/app/global/components/dialogs/informational-dialog/informational-dialog.component';
import { UserSearchResultsDialog } from '../user-search-results-dialog/user-search-results-dialog.component';
import { formatDateOnly } from 'src/app/global/utils/date/date.utils';
import { BcitiConfirmDialogComponent } from 'src/app/global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';

interface SelectOption {
    id: number;
    name: string;
    value: any;
}
@Component({
    selector: 'app-add-existing-user-dialog',
    templateUrl: './add-existing-user-dialog.component.html',
    styleUrls: ['./add-existing-user-dialog.component.scss'],
})
export class AddExistingUserDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    formLoading = false;

    submitLoading: boolean;

    maxDate = new Date();

    formGroup = new FormGroup({
        email: new FormControl('', [Validators.email, this.forbiddenSameEmailValidator(this.data?.userRecord?.email)]),
        cardCode: new FormControl('', [Validators.minLength(2)]),
        firstName: new FormControl('', [Validators.minLength(2)]),
        lastName: new FormControl('', [Validators.minLength(2)]),
        sex: new FormControl(''),
        birthDate: new FormControl(''),
        targetLink: new FormControl('', [Validators.required]),
        sourceLink: new FormControl('', [Validators.required]),
    });

    familyLinks: SelectOption[];

    selectedLink: {};

    sexOptions: SelectOption[];

    selectedSex: {};

    get email() {
        return this.formGroup.get('email');
    }
    get cardCode() {
        return this.formGroup.get('cardCode');
    }
    get firstName() {
        return this.formGroup.get('firstName');
    }
    get lastName() {
        return this.formGroup.get('lastName');
    }
    get sex() {
        return this.formGroup.get('sex');
    }
    get birthDate() {
        return this.formGroup.get('birthDate');
    }
    get targetLink() {
        return this.formGroup.get('targetLink');
    }
    get sourceLink() {
        return this.formGroup.get('sourceLink');
    }

    constructor(
        private translate: TranslateService,
        private userService: UserService,
        private dialogRef: MatDialogRef<AddExistingUserDialogComponent>,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: { userRecord: UserRecord },
    ) {
        this.dialogRef.disableClose = true;

        this.dialogRef
            .backdropClick()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.closeDialog();
            });
    }

    ngOnInit(): void {
        this.familyLinks = Object.values(FamilyLinkType).map((link, index) => ({
            id: index,
            name: this.translate.instant(`pages.user-cards.family.common.link.${link}`),
            value: link,
        }));

        this.sexOptions = Object.values(Sex).map((sex, index) => ({
            id: index,
            name: this.translate.instant(`pages.user-cards.family.common.gender.${sex.toUpperCase()}`),
            value: sex,
        }));
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    checkRequiredSearch() {
        if (!this.firstName.value && !this.lastName.value && !this.email.value && !this.cardCode.value) {
            this.formGroup.setErrors({ requiredSearch: true });
        } else {
            this.formGroup.setErrors(null);
        }
    }

    closeDialog() {
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            maxWidth: '580px',
            panelClass: 'confirm-dialog',
            data: {
                dialog: {
                    title: this.translate.instant('pages.user-cards.family.create.confirm-dialog.title'),
                    body: this.translate.instant('pages.user-cards.family.create.confirm-dialog.body'),
                    icon: '',
                    iconColor: '',
                    confirmLabel: this.translate.instant('pages.user-cards.common.button.close'),
                    cancelLabel: this.translate.instant('pages.user-cards.common.button.cancel'),
                    confirmButtonClass: 'bciti-button--red',
                    cancelButtonClass: 'bciti-button bciti-button--lightblue-inversed',
                },
                component: {},
            },
        });

        dialogRef.componentInstance.onSubmit.subscribe((e) => {
            dialogRef.close();
            if (e) {
                this.dialogRef.close();
            }
        });
    }

    linkChange(event: SelectOption) {
        this.selectedLink = event;
        this.formGroup.patchValue({ targetLink: event.value });
        this.formGroup.get('targetLink').markAsTouched();

        switch (event.value.toUpperCase()) {
            case FamilyLinkType.CHILD:
                this.formGroup.patchValue({ sourceLink: FamilyLinkType.PARENT });
                break;
            case FamilyLinkType.PARENT:
                this.formGroup.patchValue({ sourceLink: FamilyLinkType.CHILD });
                break;
            case FamilyLinkType.OTHER:
                this.formGroup.patchValue({ sourceLink: FamilyLinkType.OTHER });
                break;
            case FamilyLinkType.SPOUSE:
                this.formGroup.patchValue({ sourceLink: FamilyLinkType.SPOUSE });
                break;
        }
    }

    sexChange(option) {
        this.selectedSex = option;
        this.formGroup.patchValue({ sex: option.value });
        this.formGroup.get('sex').markAsTouched();
    }

    private forbiddenSameEmailValidator(email: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!control.value) {
                return null;
            }
            const forbidden = email === control.value;
            return forbidden ? { sameEmail: 'error' } : null;
        };
    }

    handleEmptyResults() {
        this.dialogRef.addPanelClass('is-hidden');

        const firstNameLabel = this.translate.instant('pages.user-cards.family.common.fields.firstName.label');
        const lastNameLabel = this.translate.instant('pages.user-cards.family.common.fields.lastName.label');
        const citizenCardNumber = this.translate.instant('pages.user-cards.family.common.fields.citizenCardNumber.label');
        const emailLabel = this.translate.instant('pages.user-cards.family.common.fields.email.label');

        let error = `<p>${this.translate.instant('pages.user-cards.user-search-results.errors.noResults.part1')}</p>`;

        if (this.firstName.value) {
            error += `<p>${firstNameLabel} <strong>${this.firstName.value}</strong></p>`;
        }

        if (this.lastName.value) {
            error += `<p>${lastNameLabel} <strong>${this.lastName.value}</strong></p>`;
        }

        if (this.cardCode.value) {
            error += `<p>${citizenCardNumber} <strong>${this.cardCode.value}</strong></p>`;
        }

        if (this.email.value) {
            error += `<p>${emailLabel} <strong>${this.email.value}</strong></p>`;
        }

        error += `<p>${this.translate.instant('pages.user-cards.user-search-results.errors.noResults.part2')}</p>`;

        error += `<p>${this.translate.instant('pages.user-cards.user-search-results.errors.noResults.part3')}</p>`;

        this.dialog
            .open(InformationalDialogComponent, {
                panelClass: 'bciti-error-dialog',
                data: {
                    title: this.translate.instant('pages.user-cards.user-search-results.errors.noResults.title'),
                    body: error,
                    errorSeverity: ErrorSeverity.Info,
                    hideIcon: true,
                },
            })
            .afterClosed()
            .subscribe(() => {
                this.dialogRef.removePanelClass('is-hidden');
            });
    }

    private scrollToFirstInvalidControl() {
        setTimeout(() => {
            let form = document.getElementById('idAddFamilyMemberForm');
            let firstInvalidControl = form.querySelector('.ng-invalid');
            if (firstInvalidControl) {
                firstInvalidControl.scrollIntoView({ behavior: 'smooth' });
                (firstInvalidControl as HTMLElement).focus();
            }
        }, 300);
    }

    onSubmit() {
        this.formGroup.markAllAsTouched();
        this.scrollToFirstInvalidControl();

        this.checkRequiredSearch();

        if (this.formGroup.invalid) return;

        this.submitLoading = true;

        const payload: any = {
            email: this.email.value,
            cardCode: this.cardCode.value,
            firstName: this.firstName.value,
            lastName: this.lastName.value,
            sex: this.sex.value,
            birthDate: formatDateOnly(this.birthDate.value),
        };

        this.userService
            .searchUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (results) => {
                    this.submitLoading = false;
                    if (results.length === 0) {
                        this.handleEmptyResults();
                    } else {
                        this.dialogRef.addPanelClass('is-hidden');
                        this.dialog
                            .open(UserSearchResultsDialog, {
                                panelClass: ['bciti-dialog', 'bciti-dialog--xl'],
                                data: {
                                    results: results,
                                    userRecord: this.data.userRecord,
                                    payload: {
                                        sourceLink: this.sourceLink.value,
                                        targetLink: this.targetLink.value,
                                        proofOfIdentity: [],
                                    } as PostUserFamilyLinkPayload,
                                },
                            })
                            .afterClosed()
                            .subscribe(() => {
                                this.dialogRef.removePanelClass('is-hidden');
                            });
                    }
                },
                (error) => {
                    this.submitLoading = false;
                    console.log(error);
                },
            );
    }
}
